import {Injectable} from '@angular/core';
import {SearchResultsPayload} from '../types/SearchResultsPayload';
import {SearchState} from '../types/SearchState';
import {ToolsService} from './tools.service';
import {SearchResultsTimeslotPayload} from '../types/SearchResultsTimeslotPayload';
import {SearchTimeslot} from '../types/SearchTimeslot';
import * as dayjs from 'dayjs';
import 'dayjs/locale/sv';
import 'dayjs/plugin/timezone';
import {TimeSlotFilterType} from '../enums/TimeSlotFilterTypeEnum';
import {AppConfigService} from './app-config.service';
import {RoutesAddonsEnums} from '../enums/RoutesEnums';

@Injectable({
  providedIn: 'root',
})
export class SearchHelperService {
  constructor(
    private tools: ToolsService,
    private appConfigService: AppConfigService,
  ) {
    dayjs.locale('sv');
  }

  public generateSearchPayloadFromSearchState(
    searchState: SearchState,
    noLimit: boolean,
    includeAdCampaignOwner: boolean,
    limitToOnlyOneItem: boolean,
  ): SearchResultsPayload {
    const payload: SearchResultsPayload = {
      page: searchState.page,
      pageSize: limitToOnlyOneItem === true ? 1 : searchState.pageSize,
      urlPath: encodeURIComponent(searchState.urlPath),
      caregiverUnitTypesUrlPath: searchState.caregiverUnitTypeUrlPath,
      placesUrlPath: searchState.placeUrlPath,
      careServicesTypeUrlPath: searchState.careServiceTypeUrlPath,
      symptomUrlPath: searchState.symptomUrlPath,
      freeSearch: '',
      openNow: searchState.openNow,
      openAtWeekend: searchState.openWeekend,
      location: {
        lat: searchState.locationSearch ? searchState.lat : 0,
        lon: searchState.locationSearch ? searchState.lon : 0,
      },
      distance: searchState.distance,
      sorting: searchState.sort,
      customerFeatureTypes: [],
      digitalPage: searchState.digitalPage,
      df: searchState.df,
      timeSlots: this.convertTimeslotToSearchResultsTimeslotPayload(searchState.timeslot),
      includeSearchResult: true,
      includeAdCampaignOwner: includeAdCampaignOwner,
      attributes: Object.keys(searchState.attributes)
        .filter((key) => searchState.attributes[key])
        .map((key) => Number(key)),
    };

    const freeSearchTermArr: string[] = [];
    if (searchState.freeSearchTerm !== '') {
      freeSearchTermArr.push(searchState.freeSearchTerm);
    }
    if (freeSearchTermArr.length > 0) {
      payload.freeSearch = `*${freeSearchTermArr.join(' ')}*`;
    }
    payload.freeSearch = encodeURIComponent(payload.freeSearch);

    if (!this.tools.isNil(searchState.customerFeatureType) && searchState.customerFeatureType !== '') {
      payload.customerFeatureTypes.push(searchState.customerFeatureType);
    }

    if (noLimit === true) {
      payload.page = 1;
      payload.pageSize = 9999;
      payload.digitalPage = 1;
    }

    return payload;
  }

  public isPayloadValidForSearch(payload: SearchResultsPayload): boolean {
    return (
      (!this.tools.isNil(payload.caregiverUnitTypesUrlPath) && payload.caregiverUnitTypesUrlPath !== '') ||
      (!this.tools.isNil(payload.careServicesTypeUrlPath) && payload.careServicesTypeUrlPath !== '') ||
      (!this.tools.isNil(payload.placesUrlPath) && payload.placesUrlPath !== '') ||
      (!this.tools.isNil(payload.symptomUrlPath) && payload.symptomUrlPath !== '') ||
      (!this.tools.isNil(payload.freeSearch) && payload.freeSearch !== '')
    );
  }

  public shouldAskForCampaignOwner(state: SearchState): boolean {
    return (
      (!this.tools.isNil(state.caregiverUnitTypeUrlPath) && state.caregiverUnitTypeUrlPath !== '') ||
      (!this.tools.isNil(state.careServiceTypeUrlPath) && state.careServiceTypeUrlPath !== '') ||
      (!this.tools.isNil(state.symptomUrlPath) && state.symptomUrlPath !== '') ||
      (!this.tools.isNil(state.placeUrlPath) && state.placeUrlPath !== '')
    );
  }

  public shouldLimitToOnlyOneItem(state: SearchState): boolean {
    return this.shouldDisplayServiceTypePage(state) || this.shouldDisplaySymptomPage(state);
  }

  public shouldDisplaySearchResults(state: SearchState): boolean {
    return !(
      state.careServiceTypeUrlPath !== '' &&
      state.caregiverUnitTypeUrlPath === '' &&
      state.symptomUrlPath === '' &&
      state.placeUrlPath === '' &&
      state.freeSearchTerm === '' &&
      state.forceContentPage === true &&
      state.forceSearchResults === false
    );
  }

  public shouldDisplayServiceTypePage(state: SearchState): boolean {
    return (
      ((state.careServiceTypeUrlPath !== '' &&
        state.symptomUrlPath === '' &&
        state.caregiverUnitTypeUrlPath === '' &&
        state.placeUrlPath === '' &&
        state.freeSearchTerm === '' &&
        state.page === 1 &&
        state.locationSearch === false &&
        state.openNow === false &&
        state.openWeekend === false &&
        state.customerFeatureType === '' &&
        state.timeslot === null) ||
        state.forceContentPage === true) &&
      state.forceSearchResults === false
    );
  }

  public shouldDisplaySymptomPage(state: SearchState): boolean {
    return (
      ((state.careServiceTypeUrlPath === '' &&
        state.symptomUrlPath !== '' &&
        state.caregiverUnitTypeUrlPath === '' &&
        state.placeUrlPath === '' &&
        state.freeSearchTerm === '' &&
        state.page === 1 &&
        state.locationSearch === false &&
        state.openNow === false &&
        state.openWeekend === false &&
        state.customerFeatureType === '' &&
        state.timeslot === null) ||
        state.forceContentPage === true) &&
      state.forceSearchResults === false
    );
  }

  public shouldUseServiceTypeSearchEndpoint(state: SearchState): boolean {
    return (
      (state.careServiceTypeUrlPath !== '' &&
        state.symptomUrlPath === '' &&
        state.caregiverUnitTypeUrlPath === '' &&
        state.placeUrlPath !== '' &&
        state.freeSearchTerm === '' &&
        state.page === 1 &&
        state.locationSearch === false &&
        state.openNow === false &&
        state.openWeekend === false &&
        state.customerFeatureType === '' &&
        state.timeslot === null &&
        state.tab === RoutesAddonsEnums.SERP_SERVICES) ||
      state.tab === RoutesAddonsEnums.SERP_SERVICES
    );
  }

  public getDefaultSearchTab(state: SearchState): string {
    if (
      state.careServiceTypeUrlPath !== '' &&
      state.caregiverUnitTypeUrlPath === '' &&
      state.symptomUrlPath === '' &&
      state.freeSearchTerm === ''
    ) {
      return RoutesAddonsEnums.SERP_SERVICES;
    }
    return RoutesAddonsEnums.SERP_CAREPROVIDERS;
  }

  private convertTimeslotToSearchResultsTimeslotPayload(
    timeslots: SearchTimeslot[],
  ): Array<SearchResultsTimeslotPayload> {
    const result: Array<SearchResultsTimeslotPayload> = [];
    if (timeslots && timeslots.length > 0) {
      const today = dayjs();
      const now = today.locale('sv');

      const tomorrow = now.add(1, 'day');
      const nextWeek = now.add(1, 'week');

      timeslots.forEach((timeslot) => {
        switch (timeslot) {
          case SearchTimeslot.TODAY:
            result.push({
              from: now.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              to: now.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              type: TimeSlotFilterType.Today,
            });
            break;
          case SearchTimeslot.TOMORROW:
            result.push({
              from: tomorrow.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              to: tomorrow.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              type: TimeSlotFilterType.Tomorrow,
            });
            break;
          case SearchTimeslot.THIS_WEEK:
            result.push({
              from: now.format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              to: now.endOf('week').endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              type: TimeSlotFilterType.Week,
            });
            break;
          case SearchTimeslot.NEXT_WEEK:
            result.push({
              from: nextWeek.startOf('week').startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              to: nextWeek.endOf('week').endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
              type: TimeSlotFilterType.NextWeek,
            });
            break;
        }
      });
    }
    return result;
  }
}
